import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { toast } from 'react-toastify';
import AuthContext from '../../context/authContext';
import useCopagoMatching from '../../hooks/useCopagoMatching';

const formatDuration = durationMS =>
  Number.isFinite(durationMS) ? `${Math.round(durationMS / 60000)} Min` : '—';

const formatConfidence = confidence =>
  `${Math.round(Number(confidence || 0) * 100)} %`;

function AiSuggestionChip({ suggestion, accepted, onToggle, disabled }) {
  if (!suggestion || !suggestion.articleId) return null;
  const confidenceColor = suggestion.confidence >= 0.75 ? 'success' : 'warning';
  return (
    <Tooltip title={suggestion.reason || ''}>
      <Stack direction='row' spacing={0.5} alignItems='center'>
        <Checkbox
          size='small'
          checked={accepted}
          onChange={onToggle}
          disabled={disabled}
          sx={{ p: 0.25 }}
        />
        <AutoAwesomeIcon fontSize='small' color='primary' />
        <Typography variant='body2'>
          {suggestion.articleCopagoId} – {suggestion.articleName}
        </Typography>
        <Chip
          label={formatConfidence(suggestion.confidence)}
          size='small'
          color={confidenceColor}
          variant='outlined'
        />
      </Stack>
    </Tooltip>
  );
}

function ArticleCell({ article, matchSource, suggestion, accepted, onToggle, disabled }) {
  if (!article) {
    return (
      <Stack spacing={0.5}>
        <Chip
          icon={<WarningAmberIcon />}
          label='Nicht gematcht'
          color='warning'
          size='small'
          variant='outlined'
          sx={{ alignSelf: 'flex-start' }}
        />
        <AiSuggestionChip
          suggestion={suggestion}
          accepted={accepted}
          onToggle={onToggle}
          disabled={disabled}
        />
      </Stack>
    );
  }
  return (
    <Stack direction='row' spacing={1} alignItems='center'>
      <Typography variant='body2'>
        {article.copagoId} – {article.name}
      </Typography>
      {matchSource === 'pricing' ? (
        <Chip label='von Preisliste' size='small' variant='outlined' />
      ) : null}
    </Stack>
  );
}

// Overview of the matching between Copago articles and everything that is
// actually sold. Supports AI matching suggestions: the model proposes
// articles for unmatched targets, the user reviews and saves them in bulk.
export default function CopagoArticleMatchingDialog({ open, onClose }) {
  const authCtx = useContext(AuthContext);
  const { isLoading, getMatching, aiSuggestMatches, applyMatching } = useCopagoMatching();
  const [matching, setMatching] = useState(null);
  const [onlyUnmatched, setOnlyUnmatched] = useState(false);
  const [aiSuggestions, setAiSuggestions] = useState({});
  const [accepted, setAccepted] = useState(new Set());
  const [aiLoading, setAiLoading] = useState(false);
  const [aiProgress, setAiProgress] = useState(null);
  const [saving, setSaving] = useState(false);

  const canAiMatch = authCtx.checkRight('copagoArticle:match');

  const loadMatching = useCallback(async () => {
    try {
      const data = await getMatching();
      if (data) setMatching(data);
    } catch (err) {
      // toast already shown by the hook
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (open) loadMatching();
  }, [open, loadMatching]);

  const handleClose = useCallback(() => {
    if (saving || aiLoading) return;
    setMatching(null);
    setOnlyUnmatched(false);
    setAiSuggestions({});
    setAccepted(new Set());
    onClose();
  }, [onClose, saving, aiLoading]);

  const handleAiSuggest = useCallback(async () => {
    setAiLoading(true);
    setAiProgress(null);
    try {
      const res = await aiSuggestMatches({ onlyUnmatched: true, onProgress: setAiProgress });
      const map = {};
      const acceptedNext = new Set();
      (res?.suggestions || []).forEach(s => {
        if (!s.articleId) return;
        map[s.targetId] = s;
        acceptedNext.add(s.targetId);
      });
      setAiSuggestions(map);
      setAccepted(acceptedNext);
      const count = Object.keys(map).length;
      toast.success(
        count > 0
          ? `${count} KI-Vorschlag${count === 1 ? '' : 'e'} erstellt`
          : 'Keine KI-Vorschläge gefunden'
      );
    } catch (err) {
      // toast already shown by the hook
    } finally {
      setAiLoading(false);
      setAiProgress(null);
    }
  }, [aiSuggestMatches]);

  const toggleAccepted = useCallback(targetId => {
    setAccepted(prev => {
      const next = new Set(prev);
      if (next.has(targetId)) next.delete(targetId);
      else next.add(targetId);
      return next;
    });
  }, []);

  const handleSaveAccepted = useCallback(async () => {
    const assignments = [...accepted]
      .map(targetId => {
        const s = aiSuggestions[targetId];
        return s && s.articleId ? { targetId, articleId: s.articleId } : null;
      })
      .filter(Boolean);
    if (assignments.length === 0) return;
    setSaving(true);
    try {
      const res = await applyMatching(assignments);
      toast.success(`${res?.applied ?? assignments.length} Zuordnungen gespeichert`);
      setAiSuggestions({});
      setAccepted(new Set());
      await loadMatching();
    } catch (err) {
      // toast already shown by the hook
    } finally {
      setSaving(false);
    }
  }, [accepted, aiSuggestions, applyMatching, loadMatching]);

  const acceptedCount = useMemo(
    () => [...accepted].filter(id => aiSuggestions[id]?.articleId).length,
    [accepted, aiSuggestions]
  );

  const suggestionFor = useCallback(targetId => aiSuggestions[targetId] || null, [aiSuggestions]);

  const upgrades = useMemo(() => {
    const list = matching?.upgrades || [];
    return onlyUnmatched ? list.filter(u => !u.copagoArticle) : list;
  }, [matching, onlyUnmatched]);

  const products = useMemo(() => {
    const list = matching?.products || [];
    if (!onlyUnmatched) return list;
    return list
      .map(product => ({
        ...product,
        pricing: (product.pricing || [])
          .map(pricing => ({
            ...pricing,
            priceMatrix: (pricing.priceMatrix || []).filter(entry => !entry.copagoArticle),
          }))
          .filter(pricing => !pricing.copagoArticle || pricing.priceMatrix.length > 0),
      }))
      .filter(product => product.pricing.length > 0);
  }, [matching, onlyUnmatched]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth='md' fullWidth>
      <DialogTitle>Copago-Matching-Übersicht</DialogTitle>
      <DialogContent dividers>
        {isLoading && !matching ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Stack spacing={3}>
            <Stack
              direction='row'
              spacing={1}
              alignItems='center'
              justifyContent='space-between'
              flexWrap='wrap'
              useFlexGap
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={onlyUnmatched}
                    onChange={event => setOnlyUnmatched(event.target.checked)}
                  />
                }
                label='Nur ungematchte anzeigen'
              />
              {canAiMatch ? (
                <Stack direction='row' spacing={1}>
                  <Button
                    variant='contained'
                    startIcon={
                      aiLoading ? (
                        <CircularProgress size={18} color='inherit' />
                      ) : (
                        <AutoAwesomeIcon />
                      )
                    }
                    onClick={handleAiSuggest}
                    disabled={aiLoading || saving}
                  >
                    {aiLoading && aiProgress?.total
                      ? `KI-Vorschläge: ${aiProgress.done}/${aiProgress.total} geprüft`
                      : 'KI-Vorschläge erzeugen'}
                  </Button>
                  {acceptedCount > 0 ? (
                    <Button
                      variant='outlined'
                      color='success'
                      onClick={handleSaveAccepted}
                      disabled={saving || aiLoading}
                      startIcon={saving ? <CircularProgress size={18} /> : null}
                    >
                      {`Ausgewählte speichern (${acceptedCount})`}
                    </Button>
                  ) : null}
                </Stack>
              ) : null}
            </Stack>

            {Object.keys(aiSuggestions).length > 0 ? (
              <Typography variant='caption' color='text.secondary'>
                KI-Vorschläge sind mit <AutoAwesomeIcon fontSize='inherit' /> markiert. Haken
                setzen bzw. entfernen und anschließend „Ausgewählte speichern“ klicken — erst dann
                wird das Matching übernommen.
              </Typography>
            ) : null}

            <Box>
              <Typography variant='h6' gutterBottom>
                Upgrades
              </Typography>
              {upgrades.length === 0 ? (
                <Typography variant='body2' color='text.secondary'>
                  {onlyUnmatched ? 'Alle Upgrades sind gematcht.' : 'Keine Upgrades vorhanden.'}
                </Typography>
              ) : (
                <Table size='small'>
                  <TableHead>
                    <TableRow>
                      <TableCell>Upgrade</TableCell>
                      <TableCell>Preis</TableCell>
                      <TableCell>Copago-Artikel</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {upgrades.map(upgrade => {
                      const targetId = `upgrade:${upgrade.upgradeId}`;
                      return (
                        <TableRow key={upgrade.upgradeId}>
                          <TableCell>
                            {upgrade.name}
                            {upgrade.isActive === false ? (
                              <Chip label='inaktiv' size='small' sx={{ ml: 1 }} />
                            ) : null}
                          </TableCell>
                          <TableCell>
                            {Number.isFinite(upgrade.price)
                              ? `${upgrade.price.toFixed(2)} €`
                              : '—'}
                          </TableCell>
                          <TableCell>
                            <ArticleCell
                              article={upgrade.copagoArticle}
                              suggestion={suggestionFor(targetId)}
                              accepted={accepted.has(targetId)}
                              onToggle={() => toggleAccepted(targetId)}
                              disabled={saving}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </Box>

            <Box>
              <Typography variant='h6' gutterBottom>
                Produkte / Preislisten / Preismatrix
              </Typography>
              {products.length === 0 ? (
                <Typography variant='body2' color='text.secondary'>
                  {onlyUnmatched
                    ? 'Alle Preislisten und Matrix-Einträge sind gematcht.'
                    : 'Keine Produkte vorhanden.'}
                </Typography>
              ) : (
                <Stack spacing={2}>
                  {products.map(product => (
                    <Box key={product.productId}>
                      <Typography variant='subtitle1'>
                        {product.name}{' '}
                        <Typography component='span' variant='caption' color='text.secondary'>
                          ({product.internalName})
                        </Typography>
                      </Typography>
                      <Stack spacing={1} sx={{ mt: 1 }}>
                        {(product.pricing || []).map(pricing => {
                          const pricingTargetId = `pricing:${product.productId}:${pricing.pricingId}`;
                          return (
                            <Box key={pricing.pricingId} sx={{ pl: 2 }}>
                              <Stack direction='row' spacing={1} alignItems='center'>
                                <Typography variant='body2' fontWeight='bold'>
                                  Preisliste „{pricing.list}“
                                </Typography>
                                <ArticleCell
                                  article={pricing.copagoArticle}
                                  suggestion={suggestionFor(pricingTargetId)}
                                  accepted={accepted.has(pricingTargetId)}
                                  onToggle={() => toggleAccepted(pricingTargetId)}
                                  disabled={saving}
                                />
                              </Stack>
                              {(pricing.priceMatrix || []).length > 0 ? (
                                <Table size='small' sx={{ mt: 0.5 }}>
                                  <TableHead>
                                    <TableRow>
                                      <TableCell>Kategorie</TableCell>
                                      <TableCell>Dauer</TableCell>
                                      <TableCell>Copago-Artikel</TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {pricing.priceMatrix.map(entry => {
                                      const entryTargetId = `entry:${product.productId}:${pricing.pricingId}:${entry.entryId}`;
                                      return (
                                        <TableRow key={entry.entryId}>
                                          <TableCell>{entry.category}</TableCell>
                                          <TableCell>{formatDuration(entry.durationMS)}</TableCell>
                                          <TableCell>
                                            <ArticleCell
                                              article={entry.copagoArticle}
                                              matchSource={entry.matchSource}
                                              suggestion={suggestionFor(entryTargetId)}
                                              accepted={accepted.has(entryTargetId)}
                                              onToggle={() => toggleAccepted(entryTargetId)}
                                              disabled={saving}
                                            />
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              ) : null}
                            </Box>
                          );
                        })}
                      </Stack>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            <Typography variant='caption' color='text.secondary'>
              Das Matching wird an den Upgrades (Upgrade-Verwaltung) bzw. an den Preislisten und
              Matrix-Einträgen (Produkt-Verwaltung) gepflegt. Matrix-Einträge ohne eigenen Artikel
              erben den Artikel ihrer Preisliste.
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving || aiLoading}>
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );
}
